import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadLabelBatchByIdUrl,
  zadLabelBatchCancelUrl,
  zadLabelBatchReadyUrl,
  zadLabelBatchRecordPrintUrl,
  zadLabelBatchRefreshPricesUrl,
  zadLabelBatchesUrl,
  zadLabelBatchUpdateUrl,
} from '../../api'
import {buildZadListQuery} from '../zad/query'
import {parseZadApiError} from '../zad/errors'
import {sanitizeZadLabelBatchWritePayload} from '../zad/labelBatchHelpers'
import {
  CreateZadLabelBatchRequest,
  FetchZadLabelBatchByIdRequest,
  FetchZadLabelBatchesRequest,
  UpdateZadLabelBatchRequest,
  ZadLabelBatchIdRequest,
} from './types'

export const fetchZadLabelBatches = createAsyncThunk(
  'zadLabelBatches/fetch',
  async (payload: FetchZadLabelBatchesRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadLabelBatchesUrl}?${buildZadListQuery(payload)}`)
      const {labelBatches, total, totalPages} = res.data || {}
      return {
        success: true as const,
        labelBatches: Array.isArray(labelBatches) ? labelBatches : [],
        total: typeof total === 'number' ? total : 0,
        totalPages: typeof totalPages === 'number' ? totalPages : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadLabelBatchById = createAsyncThunk(
  'zadLabelBatches/fetchById',
  async (payload: FetchZadLabelBatchByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadLabelBatchByIdUrl(payload.id))
      return {
        success: true as const,
        labelBatch: res.data.labelBatch,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadLabelBatch = createAsyncThunk(
  'zadLabelBatches/create',
  async (payload: CreateZadLabelBatchRequest, {rejectWithValue}) => {
    try {
      const body = sanitizeZadLabelBatchWritePayload(payload)
      const res = await axios.post(zadLabelBatchesUrl, body)
      return {
        success: true as const,
        labelBatch: res.data.labelBatch,
        usedTransaction: res.data.usedTransaction,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadLabelBatch = createAsyncThunk(
  'zadLabelBatches/update',
  async (payload: UpdateZadLabelBatchRequest, {rejectWithValue}) => {
    try {
      const {id, ...values} = payload
      const body = sanitizeZadLabelBatchWritePayload(values)
      const res = await axios.post(zadLabelBatchUpdateUrl(id), body)
      return {
        success: true as const,
        labelBatch: res.data.labelBatch,
        usedTransaction: res.data.usedTransaction,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const markZadLabelBatchReady = createAsyncThunk(
  'zadLabelBatches/ready',
  async (payload: ZadLabelBatchIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadLabelBatchReadyUrl(payload.id))
      return {
        success: true as const,
        labelBatch: res.data.labelBatch,
        usedTransaction: res.data.usedTransaction,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const cancelZadLabelBatch = createAsyncThunk(
  'zadLabelBatches/cancel',
  async (payload: ZadLabelBatchIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadLabelBatchCancelUrl(payload.id))
      return {
        success: true as const,
        labelBatch: res.data.labelBatch,
        usedTransaction: res.data.usedTransaction,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const recordZadLabelBatchPrint = createAsyncThunk(
  'zadLabelBatches/recordPrint',
  async (payload: ZadLabelBatchIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadLabelBatchRecordPrintUrl(payload.id))
      return {
        success: true as const,
        labelBatch: res.data.labelBatch,
        usedTransaction: res.data.usedTransaction,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const refreshZadLabelBatchPrices = createAsyncThunk(
  'zadLabelBatches/refreshPrices',
  async (payload: ZadLabelBatchIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadLabelBatchRefreshPricesUrl(payload.id))
      return {
        success: true as const,
        labelBatch: res.data.labelBatch,
        original: res.data.original || null,
        createdNewDraft: Boolean(res.data.createdNewDraft),
        usedTransaction: res.data.usedTransaction,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
